import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';

import { ReplaceIcon } from './actionIcons';
import DeleteIconButton from './DeleteIconButton';

const isLocalFile = (file) => typeof File !== 'undefined' && file instanceof File;

// The link target for a stored file (a URL, or an object carrying one in `.file`) or a picked File that is
// not uploaded yet (a blob URL, revoked when the file changes or the row goes away).
const useFileHref = (file) => {
    const [objectUrl, setObjectUrl] = React.useState(null);
    React.useEffect(() => {
        if (!isLocalFile(file) || typeof URL.createObjectURL !== 'function') {
            setObjectUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(file);
        setObjectUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);
    if (isLocalFile(file)) {
        return objectUrl;
    }
    if (typeof file === 'string') {
        return file;
    }
    return typeof file?.file === 'string' ? file.file : null;
};

// CP-5559: the dashboard's one row for an uploaded file (Martina's approved KML row). The name is a link that
// opens/downloads the file, cut with an ellipsis to the room it has, with the full name in a tooltip only when
// it is cut; then the blue Replace icon (one circle arrow, CP-5556) and the red trash (CP-5545), each with a
// tooltip. Replace/trash render only when their handler is passed; `disabled` greys both (no write access).
// `file` is what the link opens (a URL, an object with a `.file` URL, or a picked File); `onOpen` replaces the
// link for files fetched through the API. `actionsAtEnd` pushes the icons to the far end of a full-width row.
// Pass tooltipProps={{ PopperProps: { style: { zIndex } } }} inside panels that sit above the tooltip layer.
/** @type {React.FC<Record<string, any>>} */
const FileActionRow = ({
    name,
    file,
    onOpen,
    onReplace,
    onDelete,
    replaceTitle = 'Replace file',
    deleteTitle = 'Remove file',
    disabled = false,
    actionsAtEnd = false,
    tooltipProps,
    sx,
}) => {
    const href = useFileHref(file);
    const [isCut, setIsCut] = React.useState(false);
    const label = name || '';
    const linkProps = onOpen
        ? {
              href: '#',
              onClick: (ev) => {
                  ev.preventDefault();
                  onOpen();
              },
          }
        : { href: href || undefined, target: '_blank', rel: 'noopener noreferrer', download: isLocalFile(file) ? label : undefined };
    return (
        <Box sx={[{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
            <Tooltip title={isCut ? label : ''} {...tooltipProps}>
                <Link
                    {...linkProps}
                    noWrap
                    onMouseEnter={(ev) => setIsCut(ev.currentTarget.scrollWidth > ev.currentTarget.clientWidth)}
                    sx={{ display: 'block', minWidth: 0, flex: '0 1 auto' }}
                >
                    {label}
                </Link>
            </Tooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 'none', ml: actionsAtEnd ? 'auto' : 0 }}>
                {onReplace && (
                    <Tooltip title={replaceTitle} describeChild {...tooltipProps}>
                        <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                            <IconButton aria-label={replaceTitle} color="primary" size="small" disabled={disabled} onClick={onReplace}>
                                <ReplaceIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {onDelete && <DeleteIconButton title={deleteTitle} disabled={disabled} tooltipProps={tooltipProps} onClick={onDelete} />}
            </Box>
        </Box>
    );
};

export default FileActionRow;
